import { computed, useContext } from "@nuxtjs/composition-api";
import { sharedRef, useVSFContext, Logger } from "@vue-storefront/core";
import { salesCrmGetters } from "~/composables/getters";
import { COOKIES } from "~/helpers/constants";
import { ForgotPasswordResponse, InitForgotPassword } from "./types";
import { extractCurrentLocals } from '~/composables/helpers/internals';

const useSalesCrm = (id: string) => {
  // Loads context used to call API endpoint
  const context = useVSFContext();
  const { error: nuxtError, i18n } = useContext();

  // Shared ref holding the response from the API
  const result = sharedRef(null, `auth-${id}`);

  // Shared ref indicating whether any method is waiting for the data from the API
  const loading = sharedRef(false, `auth-loading-${id}`);

  // Shared ref holding errors from the methods
  const error = sharedRef(
    {
      auth: null,
    },
    `auth-error-${id}`
  );

  // handle initial call
  const crmTokenCookie = sharedRef("", COOKIES.SALESFORCE_CRM_TOKEN);
  const locale = i18n.localeProperties.code;
  const localeBaseChannelId = extractCurrentLocals(i18n)
  // const locale = localeBaseChannelId?.channelId || 'moominarabia-english-1'
  const localeBasebaseStoreId = extractCurrentLocals(i18n)?.baseStoreId  /** Simply remove the  { locale: 'en-us'} object to make the value dynamic */
  // let lang = context.$cookies.get(COOKIES.LOCALE);
  let lang = i18n.localeProperties.languageCode;
  // if (lang === "en-gb") {
  //   lang = "en";
  // } else if (lang === "fi-fi") {
  //   lang = "fi";
  // }
  // Method to call an API endpoint and update `result`, `loading` and `error` properties
  const load = async () => {

    try {
      loading.value = true;
      // check if token is already generated
      const existngSalesForceCRMToken = context.$cookies.get(
        COOKIES.SALESFORCE_CRM_TOKEN
      );
      if (
        existngSalesForceCRMToken === undefined ||
        existngSalesForceCRMToken === ""
      ) {
        result.value = await context.$vsf.$salesforceCrm.api.auth();
        // store new generated token to VSF universal cookie
        context.$cookies.set(
          COOKIES.SALESFORCE_CRM_TOKEN,
          salesCrmGetters.salesCrmToken(result.value),
          { maxAge: 60 * 60 * 6 * 1, path: "/", secure: true, httpOnly: true }
        );
        crmTokenCookie.value = salesCrmGetters.salesCrmToken(result.value);
        result.value = crmTokenCookie.value;
      } else {
        result.value = existngSalesForceCRMToken;
      }
    } catch (err) {
      error.value.auth = err;
      Logger.error(`Auth Error ${error.value.auth}`);
    } finally {
      loading.value = false;
    }
  };

  const signup = async (params) => {

    const currentLocaleObj = extractCurrentLocals(i18n);
    const countryName = currentLocaleObj?.baseCountry || 'Finland';
    params.token = params?.token
      ? params.token
      : context.$cookies.get(COOKIES.SALESFORCE_CRM_TOKEN)
        ? context.$cookies.get(COOKIES.SALESFORCE_CRM_TOKEN)
        : crmTokenCookie.value;
    if (!params.token) {
      await load();
      params.token = crmTokenCookie.value;
    }

    let body = {
      billingCountry: countryName,
      firstName: params.firstName,
      lastName: params.lastName,
      email: params.email,
      brand: context.$config.crmBrand,
      channelId: context.$config.channelId,
      registrationContext: context.$config.crmRegContext,
      registrationChannel: context.$config.crmRegChannel,
      registrationChannelType: context.$config.crmChannelType,
      source: context.$config.crmSource,
      consumerCredential: "CDC",
      marketingChannels: [
        {
          channelType: "Email",
          ChannelOptin: "Yes",
        },
      ],
    };

    try {
      loading.value = true;
      const response =
        await context.$vsf.$salesforceCrm.api.getNewsletterSignup({
          body: body,
          token: params.token,
        });
      result.value = response;
      return response;
    } catch (err) {
      error.value.api = err;
      Logger.error(`Newsletter Signup Error ${error.value.api}`);
      return { errorMessage: error.value.api };
    } finally {
      loading.value = false;
    }
  };

  const createAccount = async (params) => {
    params.token = params?.token
      ? params.token
      : context.$cookies.get(COOKIES.SALESFORCE_CRM_TOKEN)
        ? context.$cookies.get(COOKIES.SALESFORCE_CRM_TOKEN)
        : crmTokenCookie.value;
    if (!params.token) {
      await load();
      params.token = crmTokenCookie.value;
    }
    let body = {
      externalId: params.uid,
      firstName: params.firstName,
      lastName: params.lastName,
      email: params.email,
      language: params.language,
      channelId: context.$config.channelId,
      source: params.source,
      marketingProgram: context.$config.mktProgram,
      marketingChannels: [
        {
          channelType: "Email",
          ChannelOptin: params.channelOptin,
        },
      ],
      addresses: [
        {
          isPrimary: true,
          addressType: "Billing",
          countryCode: params.countryCode,
          lastname: params.lastName,
        },
      ],
    };

    try {
      loading.value = true;
      const response = await context.$vsf.$salesforceCrm.api.createAccount({
        body: body,
        token: params.token,
      });
      result.value = response;
      return response;
    } catch (err) {
      error.value.api = err;
      Logger.error(`Create Account Error ${error.value.api}`);
      return { errorMessage: error.value.api };
    } finally {
      loading.value = false;
    }
  };

  const initForgotPassword = async (
    params: InitForgotPassword
  ): Promise<ForgotPasswordResponse | { errorMessage: string }> => {

    const countryAndLocale: String =
      context.$cookies.get(COOKIES.COUNTRY) +
      "-" +
      lang;
    const fullUrl: String = context.$config.productionURL+"/" + locale + params.url;
    let token: String = params?.token
      ? params.token
      : context.$cookies.get(COOKIES.SALESFORCE_CRM_TOKEN)
        ? context.$cookies.get(COOKIES.SALESFORCE_CRM_TOKEN)
        : crmTokenCookie.value;

    if (!token) {
      await load();
      token = crmTokenCookie.value;
    }

    let body = {
      Email__c: params.email,
      Brand__c: context.$config.crmBrand,
      Environment_Prefix_URL__c: fullUrl,
      Locale__c: locale,
    };

    try {
      loading.value = true;
      const response = await context.$vsf.$salesforceCrm.api.passwordReset({
        body: body,
        token: token,
        ct: params.captchaToken
      });
      result.value = response;

      const resultFinal = {
        status: 200,
        data: {
          result: result.value,
        },
        message: "Reset Password Email Triggered",
      };
      return resultFinal;
    } catch (err) {
      error.value.api = err;
      Logger.error(`passwordReset Error ${error.value.api}`);
      return {
        errorMessage: error.value.api,
      };
    } finally {
      loading.value = false;
    }
  };

  const notifyProductBackInStock = async (params) => {

    const currentLocaleObj = extractCurrentLocals(i18n);
    const countryName = currentLocaleObj?.baseStoreCode ;
    const countryLang = currentLocaleObj?.language

    params.token = params?.token
      ? params.token
      : context.$cookies.get(COOKIES.SALESFORCE_CRM_TOKEN)
        ? context.$cookies.get(COOKIES.SALESFORCE_CRM_TOKEN)
        : crmTokenCookie.value;
    if (!params.token) {
      await load();
      params.token = crmTokenCookie.value;
    }

    let body = {
      email: params.email,
      brand: context.$config.crmBrand,
      sourceChannelId: context.$config.channelId,
      countryCode: countryName.toUpperCase(),
      languageCode:countryLang,
      productId: params.productId,
      productName: params.productName,
      baseStoreId: localeBasebaseStoreId,
      token: params.token
    };

    try {
      loading.value = true;
      const response =
        await context.$vsf.$salesforceCrm.api.notifyProductBackInStock({
          body: body,
        });
      result.value = response;
      return response;
    } catch (err) {
      error.value.api = err;
      Logger.error(`Notify back in stock Error ${error.value.api}`);
      return { errorMessage: error.value.api };
    } finally {
      loading.value = false;
    }
  };

  return {
    load,
    signup,
    createAccount,
    initForgotPassword,
    notifyProductBackInStock,
    result: computed(() => result.value),
    loading: computed(() => loading.value),
    error: computed(() => error.value),
    salesCrmGetters,
  };
};

export default useSalesCrm;
